import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import Sources from '../../components/Sources';
import FAQSection from '../../components/FAQSection';
import TableOfContents from '../../components/TableOfContents';
import Callout from '../../components/Callout';
import Breadcrumbs from '../../components/Breadcrumbs';
import { LoadDimensionsDiagram } from '../../components/Diagrams';

export default function Lastsikring() {
  const breadcrumbs = [
    { name: "Forside", path: "/" },
    { name: "Hastighed og kørsel", path: "/trailer-hastighed" },
    { name: "Lastsikring", path: "/lastsikring" }
  ];
  const tocItems = [
    { id: "hovedregel-lastsikring", title: "Færdselslovens hovedregel om lastsikring" },
    { id: "fysiske-kraefter", title: "De fysiske kræfter der skal modstås (frem, bagud, til siden)" },
    { id: "surringsmetoder", title: "Praktiske metoder: Overfaldssurring og grimesurring" },
    { id: "trailer-net-og-presenning", title: "Trailernet, presenning og løst haveaffald" },
    { id: "boeder-og-sanktioner", title: "Bødetakster ved utilstrækkelig sikring" }
  ];

  const faq = [
    {
      q: "Er det et lovkrav at bruge trailernet til haveaffald?",
      a: "Ifølge færdselslovens § 82 må intet gods kunne falde af eller hvirvle op under kørslen. Til løst haveaffald, grene, pap og sand er et fintmasket trailernet eller en lukket presenning i praksis det eneste, der opfylder lovkravet."
    },
    {
      q: "Hvor meget kraft skal en surring kunne holde til?",
      a: "Sikringen skal som minimum kunne modstå 80 % af lastens vægt fremad (ved hård opbremsning) samt 50 % af vægten bagud og til siderne. Surringsstropper skal være CE-mærkede og have en læsbar mærkeseddel med tilladt surringskraft (LC)."
    },
    {
      q: "Må man binde gods fast med almindeligt reb eller elastikker?",
      a: "Nej, elastiksnore ('blæksprutter') og almindeligt husholdningstov er ikke godkendt til lastsikring på vej. Du skal anvende godkendte spændestropper med skralde (EN 12195-2)."
    },
    {
      q: "Hvad koster det at køre med usikret last på traileren?",
      a: "Bøden for utilstrækkelig eller manglende lastsikring starter ved 1.000 kr. Hvis lasten tabes på kørebanen eller skaber konkret fare for andre trafikanter, udløser det et klip i kørekortet eller en betinget frakendelse."
    }
  ];

  const sources = [
    { title: "Færdselslovens § 82 om anbringelse og sikring af gods (LBK nr. 1324 af 18/09/2023)", source: "retsinformation.dk", url: "https://www.retsinformation.dk/eli/lta/2023/1324" },
    { title: "Bekendtgørelse om køretøjers indretning og udstyr (BEK nr. 154 af 28/01/2021)", source: "retsinformation.dk", url: "https://www.retsinformation.dk/eli/lta/2021/154" },
    { title: "Dimensionsbekendtgørelsen (BEK nr. 577 af 30/03/2021)", source: "retsinformation.dk", url: "https://www.retsinformation.dk/eli/lta/2021/577" }
  ];

  return (
    <>
      <SEO 
        title="Lastsikring af trailer: Regler, surring og trailernet" 
        description="Lær hvordan du sikrer last på traileren lovligt: færdselslovens § 82, overfaldssurring, krav til spændestropper, trailernet og bødetakster." 
        pathname="/lastsikring"
        faq={faq}
        breadcrumbs={breadcrumbs}
      />

      <article>
        <Breadcrumbs items={breadcrumbs} />
        <h1 className="text-[30px] sm:text-[36px] font-bold text-[#2E3A46] tracking-tight leading-tight mb-4">
          Lastsikring på trailer: Regler og fremgangsmåde
        </h1>

        <p className="text-[18px] text-[#1A1C1F] leading-relaxed mb-6 font-medium">
          Ifølge færdselslovens § 82 skal alt gods på en trailer være anbragt og fastgjort således, at det <strong>ikke kan forskyde sig, falde af eller støve/hvirvle ud på vejen</strong> – selv under en katastrofeopbremsning eller undvigemanøvre.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Sikringen skal kunne modstå 80 % af lastens vægt fremad og 50 % til siderne og bagud. Det kræver godkendte spændestropper med skralde eller et fast trailernet. Manglende sikring koster bøder og kan give klip i kørekortet, hvis last tabes på kørebanen.
        </p>

        <TableOfContents items={tocItems} />

        <LoadDimensionsDiagram caption="Figur 1: Principper for lastens placering på trailerens ladflade." />

        <Callout title="De 3 gyldne regler for lastsikring">
          <ul className="m-0 p-0 list-none space-y-1 font-mono text-[15px]">
            <li>1. Læg det tungeste gods forrest over akslen for at bevare positivt kugletryk.</li>
            <li>2. Brug godkendte spændestropper mærket med EN 12195-2 (ingen elastikker).</li>
            <li>3. Dæk løse materialer (haveaffald, sand, isolering) med net eller presenning.</li>
          </ul>
        </Callout>

        <h2 id="hovedregel-lastsikring" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Færdselslovens hovedregel om lastsikring
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Færdselslovens § 82, stk. 1 fastslår:
        </p>

        <blockquote className="my-4 p-4 border-l-4 border-[#B58200] bg-[#FBF3DA] text-[16px] text-[#2E3A46] italic">
          „Køretøj må ikke belæsses på en sådan måde, at føreren ikke har tilstrækkeligt udsyn eller ikke kan manøvrere betryggende. Godset skal være anbragt således, at det ikke kan frembyde fare for personer eller ejendom eller forvolde skade på vej, gade eller plads, ej heller falde af eller slæbe på jorden.“
        </blockquote>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Det er altid <strong>føreren af bilen</strong>, der er juridisk ansvarlig for, at lasten er forsvarligt sikret, inden turen påbegyndes – uanset om traileren er lånt, lejet eller pakket af andre.
        </p>

        <h2 id="fysiske-kraefter" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          De fysiske kræfter der skal modstås
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Når du kører med et vogntog, påvirkes godset af inertikræfter ved bremsning, acceleration og sving. Standarden EN 12195 opstiller præcise minimumskrav:
        </p>

        <div className="my-6 overflow-x-auto border-t border-[#B58200]">
          <table className="w-full text-left text-[15px] border-collapse">
            <thead>
              <tr className="border-b border-[#E4E1D8] font-mono text-[13px] uppercase text-[#2E3A46]">
                <th className="py-2.5 pr-4 font-semibold">Retning</th>
                <th className="py-2.5 pr-4 font-semibold">Kraftkrav (% af lastens vægt)</th>
                <th className="py-2.5 font-semibold">Trafikal situation</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E1D8] tabular-nums">
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Fremad</td>
                <td className="py-3 pr-4 font-bold text-[#B58200]">80 % (0,8 g)</td>
                <td className="py-3 text-[#5C636B]">Katastrofeopbremsning</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Til siderne</td>
                <td className="py-3 pr-4 font-semibold text-[#2E3A46]">50 % (0,5 g)</td>
                <td className="py-3 text-[#5C636B]">Hurtige sving og undvigemanøvrer</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Bagud</td>
                <td className="py-3 pr-4 font-semibold text-[#2E3A46]">50 % (0,5 g)</td>
                <td className="py-3 text-[#5C636B]">Kraftig acceleration eller kørsel op ad bakke</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2 id="surringsmetoder" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Praktiske metoder: Overfaldssurring og grimesurring
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Der skelnes i praksis mellem to grundlæggende surringsprincipper på trailere:
        </p>

        <div className="my-6 p-4 border border-[#E4E1D8] bg-[#FBFAF6] rounded-[4px] space-y-3">
          <div>
            <h3 className="text-[16px] font-bold text-[#2E3A46] mb-1 font-mono uppercase">
              1. Overfaldssurring (Friktionssurring)
            </h3>
            <p className="text-[15px] text-[#1A1C1F] m-0">
              Stroppen lægges hen over godset og spændes ned mod ladbunden i begge sider. Dette øger trykket og dermed friktionen mod bunden. Vigtigt: Kræver en høj friktion mellem last og lad (brug gerne skridsikre gummimåtter under paller og tunge kasser).
            </p>
          </div>
          <div className="border-t border-[#E4E1D8] pt-3">
            <h3 className="text-[16px] font-bold text-[#2E3A46] mb-1 font-mono uppercase">
              2. Direkte surring (Grimesurring / loop-surring)
            </h3>
            <p className="text-[15px] text-[#1A1C1F] m-0">
              Stroppen fastgøres direkte i godsets egne surringspunkter og trækker direkte mod trailerens surringsøjer. Dette anvendes typisk ved transport af maskiner, havetraktorer, biler eller tunge byggematerialer.
            </p>
          </div>
        </div>

        <h2 id="trailer-net-og-presenning" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Trailernet, presenning og løst haveaffald
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          En meget hyppig forseelse ved kørsel til genbrugspladsen er uafdækket haveaffald eller løse papkasser. Selvom grenene ser ud til at ligge tungt på ladet, vil fartvinden ved 80 km/t løfte blade, smågrene og affald ud på kørebanen.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Køb et elastisk, fintmasket <strong>trailernet</strong> med kantelastik, der hægtes på trailerens presenningsknopper, eller brug en solid flad presenning. Det tager to minutter at montere og fjerner risikoen for bøder.
        </p>

        <h2 id="boeder-og-sanktioner" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Bødetakster ved utilstrækkelig sikring
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Politiet kontrollerer jævnligt lastsikring på indfaldsveje og ved genbrugspladser:
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li><strong>Mangelfuld sikring (ingen tabt last):</strong> Bøde på typisk 1.000 kr.</li>
          <li><strong>Tab af genstande på vej:</strong> Bøde på 2.000–3.000 kr. samt erstatningsansvar for eventuelle skader på efterfølgende biler.</li>
          <li><strong>Farlig kørsel / personskade:</strong> Kan medføre klip i kørekortet eller ubetinget frakendelse ved grov uagtsomhed.</li>
        </ul>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Husk også reglerne for fremspringende gods i vores guide om <Link to="/trailer-laengde-og-bredde" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailerens længde og bredde</Link>, samt hvordan vægten fordeles rigtigt i artiklen om <Link to="/kugletryk" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">kugletryk</Link>.
        </p>

        <FAQSection items={faq} />
        <Sources links={sources} />
      </article>
    </>
  );
}
